import { useEffect, useRef, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import { MapPin, Search } from 'lucide-react';
import styles from './Form.module.css';

const GOOGLE_MAPS_KEY = import.meta.env['VITE_GOOGLE_MAPS_KEY'] as string | undefined;

export interface ResolvedAddress {
  formatted_address: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  lat: number;
  lng: number;
  /** Google place id when the Places result carries one — lets create
   *  flows store Location.google_place_id at birth instead of pasting
   *  it later on the detail page. */
  place_id?: string;
}

interface Props {
  onSelect: (addr: ResolvedAddress) => void;
  placeholder?: string;
  /** Controlled text (New job, Warren, 21 October 2026): the field is also typed into and read without a suggestion. */
  value?: string;
  onTextChange?: (text: string) => void;
  /** With no Places key, a plain input instead of nothing: the typed line is read into its parts. */
  fallback?: boolean;
  id?: string;
}

/**
 * Address autocomplete input wrapping Google Places.
 * Renders as a regular text input until a place is selected, then fires
 * onSelect with the parsed components. Falls back to nothing if no API key
 * is configured (caller should expose manual fields as a fallback), or to a
 * plain input when the caller asks for one (fallback).
 */
export function PlaceAutocomplete(props: Props) {
  if (!GOOGLE_MAPS_KEY) return props.fallback ? <AddressInput {...props} /> : null;
  return (
    <APIProvider apiKey={GOOGLE_MAPS_KEY} libraries={['places']}>
      <PlaceAutocompleteInner {...props} />
    </APIProvider>
  );
}

function AddressInput({ placeholder = 'Search address...', value, onTextChange, id, inputRef }: Props & { inputRef?: React.RefObject<HTMLInputElement | null> }) {
  const controlled = value !== undefined;
  return (
    <div style={{ position: 'relative' }}>
      <Search
        size={14}
        style={{
          position: 'absolute', left: 12, top: '50%',
          transform: 'translateY(-50%)', color: 'var(--color-tx-m)', pointerEvents: 'none',
        }}
      />
      <input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={placeholder}
        className={styles.input}
        style={{ paddingLeft: 36 }}
        autoComplete="off"
        {...(controlled ? {
          value, onChange: (e: React.ChangeEvent<HTMLInputElement>) => onTextChange?.(e.target.value),
          // Enter picks a suggestion; it never submits the form around the field.
          onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') e.preventDefault(); },
        } : {})}
      />
    </div>
  );
}

function PlaceAutocompleteInner(props: Props) {
  const { onSelect } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const placesLibrary = useMapsLibrary('places');
  const [autocomplete, setAutocomplete] = useState<google.maps.places.Autocomplete | null>(null);

  useEffect(() => {
    if (!placesLibrary || !inputRef.current) return;
    const ac = new placesLibrary.Autocomplete(inputRef.current, {
      fields: ['address_components', 'formatted_address', 'geometry', 'place_id'],
      types: ['address'],
    });
    setAutocomplete(ac);
    return () => {
      // Google sets some listeners — clean up
      google.maps.event.clearInstanceListeners(ac);
    };
  }, [placesLibrary]);

  useEffect(() => {
    if (!autocomplete) return;
    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const addr = parsePlace(place);
      if (addr) {
        onSelect(addr);
        // Don't blow away the formatted text — but reset our internal state
      }
    });
    return () => listener.remove();
  }, [autocomplete, onSelect]);

  return <AddressInput {...props} inputRef={inputRef} />;
}

/** Convert a google.maps.places.PlaceResult into our ResolvedAddress shape. */
function parsePlace(place: google.maps.places.PlaceResult): ResolvedAddress | null {
  if (!place.geometry?.location || !place.address_components) return null;

  const get = (type: string, short = false): string => {
    const comp = place.address_components!.find((c) => c.types.includes(type));
    return comp ? (short ? comp.short_name : comp.long_name) : '';
  };

  const streetNumber = get('street_number');
  const route = get('route');
  const street = [streetNumber, route].filter(Boolean).join(' ');
  const city = get('locality') || get('postal_town') || get('sublocality') || get('administrative_area_level_2');
  const state = get('administrative_area_level_1', true);
  const zip = get('postal_code');
  const country = get('country', true) || 'US';

  return {
    formatted_address: place.formatted_address ?? '',
    street,
    city,
    state,
    zip,
    country,
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng(),
    ...(place.place_id ? { place_id: place.place_id } : {}),
  };
}

/** Optional helper for showing a coordinate label */
export function CoordsLabel({ lat, lng }: { lat: number; lng: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--color-tx-m)', fontVariantNumeric: 'tabular-nums' }}>
      <MapPin size={12} /> {lat.toFixed(6)}, {lng.toFixed(6)}
    </span>
  );
}
